
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Alert,
  Stack
} from '@mui/material';
import { fetchWithDemoFallback } from '../../../helpers/apiHelper';
import ASTITitle from '../../labels/ASTITitle';
import ASTIText from '../../labels/ASTIText';

const TRANSACTION_COLUMNS = [
  { key: 'transactiondate', label: 'Transaction Date' },
  { key: 'date_added', label: 'Date Added' },
  { key: 'description', label: 'Description' },
  { key: 'source', label: 'Source' },
  { key: 'amount', label: 'Amount' },
  { key: 'auto_category', label: 'Auto Category' },
  { key: 'manual_category', label: 'Manual Category' },
  { key: 'final_category', label: 'Final Category' },
  { key: 'division', label: 'Division' },
  { key: 'group', label: 'Group' },
  { key: 'class', label: 'Class' },
  { key: 'auto_exclude', label: 'Auto Exclude' },
  { key: 'manual_exclude', label: 'Manual Exclude' },
  { key: 'comment', label: 'Comment' }
];

export default function TransactionsPage() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState({});
  const [updateError, setUpdateError] = useState('');
  const [sortField, setSortField] = useState('transactiondate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pageInput, setPageInput] = useState('1');
  const [descriptionFilter, setDescriptionFilter] = useState(() => {
    try { return localStorage.getItem('transactionsDescriptionFilter') || ''; } catch (e) { return ''; }
  });
  const [transactionDateFilter, setTransactionDateFilter] = useState(() => {
    try { return localStorage.getItem('transactionsTransactionDateFilter') || ''; } catch (e) { return ''; }
  });
  const [dateAddedFilter, setDateAddedFilter] = useState(() => {
    try { return localStorage.getItem('transactionsDateAddedFilter') || ''; } catch (e) { return ''; }
  });
  const [sourceFilter, setSourceFilter] = useState(() => {
    try { return localStorage.getItem('transactionsSourceFilter') || 'all'; } catch (e) { return 'all'; }
  });
  const [amountFilter, setAmountFilter] = useState(() => {
    try { return localStorage.getItem('transactionsAmountFilter') || ''; } catch (e) { return ''; }
  });
  const [autoCategoryFilter, setAutoCategoryFilter] = useState(() => {
    try { return localStorage.getItem('transactionsAutoCategoryFilter') || ''; } catch (e) { return ''; }
  });
  const [manualCategoryFilter, setManualCategoryFilter] = useState(() => {
    try { return localStorage.getItem('transactionsManualCategoryFilter') || ''; } catch (e) { return ''; }
  });
  const [categoryFilter, setCategoryFilter] = useState(() => {
    try { return localStorage.getItem('transactionsCategoryFilter') || 'all'; } catch (e) { return 'all'; }
  });
  const [divisionFilter, setDivisionFilter] = useState(() => {
    try { return localStorage.getItem('transactionsDivisionFilter') || 'all'; } catch (e) { return 'all'; }
  });
  const [groupFilter, setGroupFilter] = useState(() => {
    try { return localStorage.getItem('transactionsGroupFilter') || 'all'; } catch (e) { return 'all'; }
  });
  const [classFilter, setClassFilter] = useState(() => {
    try { return localStorage.getItem('transactionsClassFilter') || ''; } catch (e) { return ''; }
  });
  const [excludeFilter, setExcludeFilter] = useState(() => {
    try { return localStorage.getItem('transactionsExcludeFilter') || 'all'; } catch (e) { return 'all'; }
  });
  const [rowFilter, setRowFilter] = useState(() => {
    try { return localStorage.getItem('transactionsRowFilter') || 'all'; } catch (e) { return 'all'; }
  });
  const [manualCategoryOptions, setManualCategoryOptions] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  // Persist filters to localStorage
  useEffect(() => {
    try { localStorage.setItem('transactionsDescriptionFilter', descriptionFilter); } catch (e) {}
  }, [descriptionFilter]);
  useEffect(() => {
    try { localStorage.setItem('transactionsTransactionDateFilter', transactionDateFilter); } catch (e) {}
  }, [transactionDateFilter]);
  useEffect(() => {
    try { localStorage.setItem('transactionsDateAddedFilter', dateAddedFilter); } catch (e) {}
  }, [dateAddedFilter]);
  useEffect(() => {
    try { localStorage.setItem('transactionsSourceFilter', sourceFilter); } catch (e) {}
  }, [sourceFilter]);
  useEffect(() => {
    try { localStorage.setItem('transactionsAmountFilter', amountFilter); } catch (e) {}
  }, [amountFilter]);
  useEffect(() => {
    try { localStorage.setItem('transactionsAutoCategoryFilter', autoCategoryFilter); } catch (e) {}
  }, [autoCategoryFilter]);
  useEffect(() => {
    try { localStorage.setItem('transactionsManualCategoryFilter', manualCategoryFilter); } catch (e) {}
  }, [manualCategoryFilter]);
  useEffect(() => {
    try { localStorage.setItem('transactionsCategoryFilter', categoryFilter); } catch (e) {}
  }, [categoryFilter]);
  useEffect(() => {
    try { localStorage.setItem('transactionsDivisionFilter', divisionFilter); } catch (e) {}
  }, [divisionFilter]);
  useEffect(() => {
    try { localStorage.setItem('transactionsGroupFilter', groupFilter); } catch (e) {}
  }, [groupFilter]);
  useEffect(() => {
    try { localStorage.setItem('transactionsClassFilter', classFilter); } catch (e) {}
  }, [classFilter]);
  useEffect(() => {
    try { localStorage.setItem('transactionsExcludeFilter', excludeFilter); } catch (e) {}
  }, [excludeFilter]);
  useEffect(() => {
    try { localStorage.setItem('transactionsRowFilter', rowFilter); } catch (e) {}
  }, [rowFilter]);
  // Change this if needed. Prefer env vars when available.
  const API_BASE =
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) ||
    process.env.REACT_APP_API_BASE_URL ||
    '';

  const getNormalizedSource = useCallback((tx) => {
    const raw = String(tx?.source || '').trim().toLowerCase();
    return raw || 'abn';
  }, []);

  const getSourceLabel = useCallback((source) => {
    const normalized = String(source || '').trim().toLowerCase();
    if (normalized === 'abn') return 'ABN';
    if (normalized === 'ics') return 'ICS';
    if (!normalized) return 'ABN';
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }, []);

  const formatDateDisplay = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return '';

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;

    if (/^\d{8}$/.test(raw)) {
      const yyyy = raw.slice(0, 4);
      const mm = raw.slice(4, 6);
      const dd = raw.slice(6, 8);
      return `${dd}/${mm}/${yyyy}`;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [yyyy, mm, dd] = raw.split('-');
      return `${dd}/${mm}/${yyyy}`;
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return raw;
    const yyyy = parsed.getFullYear();
    const mm = String(parsed.getMonth() + 1).padStart(2, '0');
    const dd = String(parsed.getDate()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy}`;
  };

  const toComparableDate = useCallback((value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return '';

    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
      const [dd, mm, yyyy] = raw.split('/');
      return `${yyyy}-${mm}-${dd}`;
    }
    if (/^\d{8}$/.test(raw)) {
      const yyyy = raw.slice(0, 4);
      const mm = raw.slice(4, 6);
      const dd = raw.slice(6, 8);
      return `${yyyy}-${mm}-${dd}`;
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return raw;
    const yyyy = parsed.getFullYear();
    const mm = String(parsed.getMonth() + 1).padStart(2, '0');
    const dd = String(parsed.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const sortTransactions = useCallback(
    (items, field, direction) => {
      const compare = (a, b) => {
        const rawA = a?.[field];
        const rawB = b?.[field];

        if (field === 'amount') {
          const numA = Number(rawA || 0);
          const numB = Number(rawB || 0);
          return numA - numB;
        }

        if (field === 'transactiondate' || field === 'date_added') {
          const valA =
            field === 'transactiondate'
              ? toComparableDate(a?.transactiondate_iso ?? rawA)
              : String(rawA || '');
          const valB =
            field === 'transactiondate'
              ? toComparableDate(b?.transactiondate_iso ?? rawB)
              : String(rawB || '');
          const dateComparison = valA.localeCompare(valB);
          if (dateComparison !== 0) {
            return dateComparison;
          }

          const amountA = Number(a?.amount ?? 0);
          const amountB = Number(b?.amount ?? 0);
          return amountA - amountB;
        }

        return String(rawA || '').localeCompare(String(rawB || ''));
      };

      const sorted = [...items].sort((a, b) => {
        if (field === 'transactiondate' || field === 'date_added') {
          const rawA = a?.[field];
          const rawB = b?.[field];
          const valA =
            field === 'transactiondate'
              ? toComparableDate(a?.transactiondate_iso ?? rawA)
              : String(rawA || '');
          const valB =
            field === 'transactiondate'
              ? toComparableDate(b?.transactiondate_iso ?? rawB)
              : String(rawB || '');

          const dateComparison = valA.localeCompare(valB);
          if (dateComparison !== 0) {
            return direction === 'desc' ? -dateComparison : dateComparison;
          }

          const amountA = Number(a?.amount ?? 0);
          const amountB = Number(b?.amount ?? 0);
          return amountA - amountB;
        }

        const result = compare(a, b);
        return direction === 'desc' ? -result : result;
      });
      return sorted;
    },
    [toComparableDate]
  );

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetchWithDemoFallback(`${API_BASE}/api/transactions`, {
        method: 'GET',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch transactions (HTTP ${response.status})`);
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        throw new Error('API did not return an array. Expected a list of transactions.');
      }

      setTransactions(sortTransactions(data, sortField, sortDirection));
    } catch (e) {
      setTransactions([]);
      setError(e?.message || 'Unknown error while loading transactions.');
    } finally {
      setLoading(false);
    }
  }, [API_BASE, sortDirection, sortField, sortTransactions]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    fetchWithDemoFallback(`${API_BASE}/api/categories`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setManualCategoryOptions(data.sort((a, b) =>
            a.localeCompare(b)
          ));
        }
      })
      .catch(() => {});
  }, [API_BASE]);

  useEffect(() => {
    if (!loading && transactions.length > 0) {
      setTransactions((prev) => sortTransactions(prev, sortField, sortDirection));
    }
  }, [sortDirection, sortField, sortTransactions, loading, transactions]);

  const columns = useMemo(() => TRANSACTION_COLUMNS, []);

  const sourceOptions = useMemo(() => {
    const values = Array.from(
      new Set(
        transactions.map((tx) => getNormalizedSource(tx))
      )
    );
    return values.sort((a, b) => a.localeCompare(b));
  }, [transactions, getNormalizedSource]);

  const categoryOptions = useMemo(() => {
    const values = Array.from(
      new Set(
        transactions
          .map((tx) => String(tx?.final_category || tx?.auto_category || '').trim())
          .filter(Boolean)
      )
    );
    return values.sort((a, b) => a.localeCompare(b));
  }, [transactions]);

  const divisionOptions = useMemo(() => {
    const values = Array.from(
      new Set(
        transactions
          .map((tx) => String(tx?.division || '').trim())
          .filter(Boolean)
      )
    );
    return values.sort((a, b) => a.localeCompare(b));
  }, [transactions]);

  const groupOptions = useMemo(() => {
    const values = Array.from(
      new Set(
        transactions
          .map((tx) => String(tx?.group || '').trim())
          .filter(Boolean)
      )
    );
    return values.sort((a, b) => a.localeCompare(b));
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    const descriptionNeedle = descriptionFilter.trim().toLowerCase();
    const transactionDateNeedle = transactionDateFilter.trim().toLowerCase();
    const dateAddedNeedle = dateAddedFilter.trim().toLowerCase();
    const amountNeedle = amountFilter.trim().toLowerCase();
    const autoCategoryNeedle = autoCategoryFilter.trim().toLowerCase();
    const manualCategoryNeedle = manualCategoryFilter.trim().toLowerCase();
    const classNeedle = classFilter.trim().toLowerCase();

    return transactions.filter((tx) => {
      const description = String(tx?.description || '').toLowerCase();
      const txDate = formatDateDisplay(tx?.transactiondate ?? tx?.Transactiedatum).toLowerCase();
      const txDateAdded = formatDateDisplay(tx?.date_added).toLowerCase();
      const source = getNormalizedSource(tx);
      const amount = String(tx?.amount ?? '').toLowerCase();
      const autoCategory = String(tx?.auto_category || '').toLowerCase();
      const manualCategory = String(tx?.manual_category || '').toLowerCase();
      const finalCategory = String(tx?.final_category || tx?.auto_category || '').trim();
      const division = String(tx?.division || '').trim();
      const group = String(tx?.group || '').trim();
      const classValue = String(tx?.class || '').toLowerCase();
      const isExcluded = Boolean(tx?.final_exclude);

      if (transactionDateNeedle && !txDate.includes(transactionDateNeedle)) {
        return false;
      }

      if (dateAddedNeedle && !txDateAdded.includes(dateAddedNeedle)) {
        return false;
      }

      if (descriptionNeedle && !description.includes(descriptionNeedle)) {
        return false;
      }

      if (amountNeedle && !amount.includes(amountNeedle)) {
        return false;
      }

      if (autoCategoryNeedle && !autoCategory.includes(autoCategoryNeedle)) {
        return false;
      }

      if (manualCategoryNeedle && !manualCategory.includes(manualCategoryNeedle)) {
        return false;
      }

      if (sourceFilter !== 'all' && source !== sourceFilter) {
        return false;
      }

      if (categoryFilter !== 'all' && finalCategory !== categoryFilter) {
        return false;
      }

      if (divisionFilter !== 'all' && division !== divisionFilter) {
        return false;
      }

      if (groupFilter !== 'all' && group !== groupFilter) {
        return false;
      }

      if (classNeedle && !classValue.includes(classNeedle)) {
        return false;
      }

      if (excludeFilter === 'included' && isExcluded) {
        return false;
      }

      if (excludeFilter === 'excluded' && !isExcluded) {
        return false;
      }

      const txId = String(tx?._id ?? tx?.id ?? '').trim();
      if (rowFilter === 'selected' && !selectedIds.has(txId)) {
        return false;
      }

      if (rowFilter === 'unselected' && selectedIds.has(txId)) {
        return false;
      }

      return true;
    });
  }, [
    transactions,
    descriptionFilter,
    transactionDateFilter,
    dateAddedFilter,
    sourceFilter,
    amountFilter,
    autoCategoryFilter,
    manualCategoryFilter,
    categoryFilter,
    divisionFilter,
    groupFilter,
    classFilter,
    excludeFilter,
    rowFilter,
    selectedIds,
    getNormalizedSource
  ]);

  const filteredIds = useMemo(
    () => filteredTransactions.map((tx) => String(tx?._id ?? tx?.id ?? '').trim()).filter(Boolean),
    [filteredTransactions]
  );
  const selectedFilteredCount = useMemo(
    () => filteredIds.reduce((count, id) => (selectedIds.has(id) ? count + 1 : count), 0),
    [filteredIds, selectedIds]
  );

  const selectedTransactionsSum = useMemo(
    () => {
      let sum = 0;
      selectedIds.forEach((id) => {
        const tx = transactions.find((t) => String(t?._id ?? t?.id ?? '') === id);
        if (tx) {
          const amount = parseFloat(tx.amount ?? 0);
          sum += isNaN(amount) ? 0 : amount;
        }
      });
      return sum;
    },
    [selectedIds, transactions]
  );
  const allFilteredSelected = filteredIds.length > 0 && selectedFilteredCount === filteredIds.length;

  const totalPages = Math.max(1, Math.ceil(filteredTransactions.length / rowsPerPage));

  useEffect(() => {
    setPageInput(String(page + 1));
  }, [page]);

  const handlePageInputCommit = useCallback(() => {
    const parsed = parseInt(pageInput, 10);
    if (!Number.isFinite(parsed) || Number.isNaN(parsed)) {
      setPageInput(String(page + 1));
      return;
    }
    const clamped = Math.min(Math.max(parsed, 1), totalPages);
    setPage(clamped - 1);
    setPageInput(String(clamped));
  }, [pageInput, page, totalPages]);

  const handleSelectAllFiltered = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredIds.forEach((id) => next.add(id));
      return next;
    });
  }, [filteredIds]);

  const handleClearFilteredSelection = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredIds.forEach((id) => next.delete(id));
      return next;
    });
  }, [filteredIds]);

  const renderCellValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const getDisplayedCellValue = (tx, col) => {
    if (col === 'transactiondate' || col === 'Transactiedatum') {
      return formatDateDisplay(tx?.transactiondate ?? tx?.Transactiedatum);
    }
    if (col === 'date_added') {
      return formatDateDisplay(tx?.date_added);
    }
    if (col === 'source') {
      return getSourceLabel(getNormalizedSource(tx));
    }
    return renderCellValue(tx?.[col]);
  };

  const handleManualCategorySave = useCallback(
    async (tx, value) => {
      const rawId = tx?._id ?? tx?.id;
      if (!rawId) return;

      const id = String(rawId);
      const manualCategory = String(value ?? '').trim();

      setUpdating((prev) => ({ ...prev, [id]: true }));
      setUpdateError('');

      try {
        const response = await fetchWithDemoFallback(`${API_BASE}/api/transactions/${encodeURIComponent(id)}/manual-category`, {
          method: 'PATCH',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ manualCategory })
        });

        if (!response.ok) {
          const json = await response.json().catch(() => ({}));
          throw new Error(json?.error || `Failed to update manual category (HTTP ${response.status})`);
        }

        const updated = await response.json();
        setTransactions((prev) =>
          prev.map((item) => (String(item?._id ?? item?.id ?? '') === id ? { ...item, ...updated } : item))
        );
      } catch (e) {
        setUpdateError(e?.message || 'Unable to update manual category.');
      } finally {
        setUpdating((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    },
    [API_BASE]
  );

  const handleCommentSave = useCallback(
    async (tx, value) => {
      const rawId = tx?._id ?? tx?.id;
      if (!rawId) return;

      const id = String(rawId);
      const comment = String(value ?? '').trim();

      setUpdating((prev) => ({ ...prev, [id]: true }));
      setUpdateError('');

      try {
        const response = await fetchWithDemoFallback(`${API_BASE}/api/transactions/${encodeURIComponent(id)}/comment`, {
          method: 'PATCH',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ comment })
        });

        if (!response.ok) {
          const json = await response.json().catch(() => ({}));
          throw new Error(json?.error || `Failed to update comment (HTTP ${response.status})`);
        }

        const updated = await response.json();
        setTransactions((prev) =>
          prev.map((item) => (String(item?._id ?? item?.id ?? '') === id ? { ...item, ...updated } : item))
        );
      } catch (e) {
        setUpdateError(e?.message || 'Unable to update comment.');
      } finally {
        setUpdating((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    },
    [API_BASE]
  );

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} transaction(s)? This cannot be undone.`)) return;
    setDeleting(true);
    setUpdateError('');
    try {
      const response = await fetchWithDemoFallback(`${API_BASE}/api/transactions`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds) })
      });
      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        throw new Error(json?.error || `Failed to delete (HTTP ${response.status})`);
      }
      setTransactions((prev) => prev.filter((tx) => !selectedIds.has(String(tx?._id ?? tx?.id ?? ''))));
      setSelectedIds(new Set());
    } catch (e) {
      setUpdateError(e?.message || 'Unable to delete transactions.');
    } finally {
      setDeleting(false);
    }
  }, [API_BASE, selectedIds]);


  const handleToggleManualExclude = useCallback(
    async (tx, checked) => {
      const rawId = tx?._id ?? tx?.id;
      if (!rawId) {
        setUpdateError('Transaction ID missing.');
        return;
      }
      const id = String(rawId);

      const newManualExcludeState = typeof checked === 'boolean' ? checked : !Boolean(tx.manual_exclude);
      setUpdateError('');
      setUpdating((prev) => ({ ...prev, [id]: true }));

      try {
        console.log('PATCH manual-exclude:', { id, manualExclude: newManualExcludeState });
        const encodedId = encodeURIComponent(id);
        const response = await fetchWithDemoFallback(`${API_BASE}/api/transactions/${encodedId}/manual-exclude`, {
          method: 'PATCH',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ manualExclude: newManualExcludeState })
        });

        if (!response.ok) {
          const json = await response.json().catch(() => ({}));
          throw new Error(json?.error || `Failed to update manual exclude (HTTP ${response.status})`);
        }

        const updated = await response.json();
        setTransactions((prev) =>
          prev.map((item) => {
            const itemId = String(item?._id ?? item?.id ?? '');
            return itemId === id ? { ...item, manual_exclude: updated.manual_exclude, final_exclude: updated.final_exclude } : item;
          })
        );

        // persist a last-updated marker so other views can detect changes even if not mounted
        try { localStorage.setItem('transactionsLastUpdated', String(Date.now())); } catch (e) {}
        window.dispatchEvent(new CustomEvent('transactionsUpdated', { detail: { id, manual_exclude: updated.manual_exclude } }));
        // fetchTransactions() removed - local state update is sufficient
      } catch (e) {
        setUpdateError(e?.message || 'Unable to update manual exclude.');
      } finally {
        setUpdating((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    },
    [API_BASE]
  );

  return (
    <Box
      width="100vw"
      minHeight="100vh"
      padding="20px"
      paddingBottom="20px"
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
      sx={{ backgroundColor: '#FFFFFF', color: '#2B1810', overflowY: 'hidden', overflowX: 'hidden', height: '100vh' }}
    >
      <Box sx={{ width: '100%', maxWidth: '100%', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ flexShrink: 0, position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#FFFFFF', paddingBottom: 2 }}>
          <Box>
            <ASTITitle text="Transactions" />
            <ASTIText text="Showing live data from local transactions database" />
          </Box>

          <Stack direction="row" alignItems="center" spacing={2}>
            <Button
              variant="outlined"
              onClick={handleSelectAllFiltered}
              disabled={loading || filteredIds.length === 0 || allFilteredSelected}
            >
              Select All Filtered
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearFilteredSelection}
              disabled={loading || selectedFilteredCount === 0}
            >
              Clear Filtered Selection
            </Button>
            <Button
              variant="contained"
              onClick={handleDeleteSelected}
              disabled={deleting || selectedIds.size === 0}
              sx={{ backgroundColor: '#c62828' }}
            >
              Delete {selectedIds.size} selected
            </Button>
            <Button
              variant="contained"
              onClick={fetchTransactions}
              sx={{ backgroundColor: '#C47A30' }}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>

        {selectedIds.size > 0 && (
          <Box sx={{ 
            p: 1.5, 
            backgroundColor: '#f5f5f5', 
            border: '1px solid #ddd', 
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <strong>Selected Transactions Sum:</strong>
            <Box sx={{ fontSize: '16px', fontWeight: 'bold', color: selectedTransactionsSum >= 0 ? '#2B1810' : '#c62828' }}>
              {selectedTransactionsSum.toFixed(2)} €
            </Box>
            <Box sx={{ fontSize: '12px', color: '#666' }}>
              ({selectedIds.size} transactions)
            </Box>
          </Box>
        )}

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          {loading && (
            <Box display="flex" alignItems="center" gap={2} sx={{ color: '#2B1810' }}>
              <CircularProgress size={20} />
              Loading transactions from local database…
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && transactions.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No transactions found in local database.
            </Alert>
          )}

          {!loading && !error && transactions.length > 0 && filteredTransactions.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No transactions match the selected filters.
            </Alert>
          )}

          {!loading && !error && updateError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {updateError}
            </Alert>
          )}

          {!loading && !error && transactions.length > 0 && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setTransactionDateFilter('');
                  setDateAddedFilter('');
                  setDescriptionFilter('');
                  setSourceFilter('all');
                  setAmountFilter('');
                  setAutoCategoryFilter('');
                  setManualCategoryFilter('');
                  setCategoryFilter('all');
                  setDivisionFilter('all');
                  setGroupFilter('all');
                  setClassFilter('');
                  setExcludeFilter('all');
                  setRowFilter('all');
                  setPage(0);
                }}
              >
                Clear Filters
              </Button>
            </Stack>
          )}

          {!loading && !error && filteredTransactions.length > 0 && (
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', mt: 2, border: '1px solid #D4B896', borderRadius: '8px' }}>
              <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <Table size="small" stickyHeader sx={{ backgroundColor: '#FFFFFF', color: '#2B1810', minWidth: 2400 }}>
                  <TableHead>
                    <TableRow sx={{ position: 'sticky', top: 0, zIndex: 6 }}>
                      <TableCell
                        padding="checkbox"
                        sx={{ backgroundColor: '#F0E3D0', borderColor: '#D4B896' }}
                      >
                        <Checkbox
                          indeterminate={
                            selectedIds.size > 0 &&
                            selectedIds.size < filteredTransactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length
                          }
                          checked={
                            filteredTransactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length > 0 &&
                            filteredTransactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).every(
                              (tx) => selectedIds.has(String(tx?._id ?? tx?.id ?? ''))
                            )
                          }
                          onChange={(e) => {
                            const pageIds = filteredTransactions
                              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                              .map((tx) => String(tx?._id ?? tx?.id ?? ''));
                            setSelectedIds((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) pageIds.forEach((id) => next.add(id));
                              else pageIds.forEach((id) => next.delete(id));
                              return next;
                            });
                          }}
                          sx={{ color: '#2B1810', '&.Mui-checked': { color: '#F4A7B9' }, '&.MuiCheckbox-indeterminate': { color: '#F4A7B9' } }}
                        />
                      </TableCell>
                      {columns.map((col) => (
                        <TableCell
                          key={col.key}
                          sortDirection={sortField === col.key ? sortDirection : false}
                          sx={{
                            fontWeight: 'bold',
                            color: '#2B1810',
                            backgroundColor: '#F0E3D0',
                            borderColor: '#D4B896',
                            fontSize: '12px',
                            padding: '8px',
                            whiteSpace: ['transactiondate', 'date_added'].includes(col.key) ? 'normal' : 'nowrap',
                            maxWidth: col.key === 'description' ? 280 : ['transactiondate', 'date_added'].includes(col.key) ? 120 : ['source', 'amount'].includes(col.key) ? 65 : 160,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            cursor: 'pointer',
                            userSelect: 'none',
                            lineHeight: ['transactiondate', 'date_added'].includes(col.key) ? 1.2 : 'normal'
                          }}
                          title={col.label}
                        >
                          <TableSortLabel
                            active={sortField === col.key}
                            direction={sortField === col.key ? sortDirection : 'asc'}
                            onClick={() => {
                              if (sortField === col.key) {
                                setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                              } else {
                                setSortField(col.key);
                                setSortDirection('asc');
                              }
                              setPage(0);
                            }}
                            sx={{
                              color: '#2B1810 !important',
                              '& .MuiTableSortLabel-icon': { color: '#7A5035 !important' },
                              '&.Mui-active': { color: '#F4A7B9 !important' },
                              '&.Mui-active .MuiTableSortLabel-icon': { color: '#F4A7B9 !important' }
                            }}
                          >
                            {col.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow sx={{ position: 'sticky', top: 40, zIndex: 5 }}>
                      <TableCell sx={{ backgroundColor: '#F9F2E8', borderColor: '#D4B896', verticalAlign: 'top' }}>
                        <TextField
                          size="small"
                          select
                          SelectProps={{ native: true }}
                          value={rowFilter}
                          onChange={(e) => {
                            setRowFilter(e.target.value);
                            setPage(0);
                          }}
                          sx={{
                            minWidth: 60,
                            '& .MuiInputBase-input': { color: '#2B1810', fontSize: '12px', py: 0.5 },
                            '& option': { color: '#111', backgroundColor: '#fff' }
                          }}
                        >
                          <option value="all">All</option>
                          <option value="selected">Selected</option>
                          <option value="unselected">Unselected</option>
                        </TextField>
                      </TableCell>
                      {columns.map((col) => (
                        <TableCell
                          key={`filter-${col.key}`}
                          sx={{ backgroundColor: '#F9F2E8', borderColor: '#D4B896', p: '6px 8px', verticalAlign: 'top' }}
                        >
                          {col.key === 'source' ? (
                            <TextField
                              size="small"
                              select
                              SelectProps={{ native: true }}
                              value={sourceFilter}
                              onChange={(e) => {
                                setSourceFilter(e.target.value);
                                setPage(0);
                              }}
                              sx={{
                                minWidth: 120,
                                '& .MuiInputBase-input': { color: '#2B1810', fontSize: '12px', py: 0.5 },
                                '& option': { color: '#111', backgroundColor: '#fff' }
                              }}
                            >
                              <option value="all">All</option>
                              {sourceOptions.map((option) => (
                                <option key={option} value={option}>{getSourceLabel(option)}</option>
                              ))}
                            </TextField>
                          ) : col.key === 'final_category' ? (
                            <TextField
                              size="small"
                              select
                              SelectProps={{ native: true }}
                              value={categoryFilter}
                              onChange={(e) => {
                                setCategoryFilter(e.target.value);
                                setPage(0);
                              }}
                              sx={{
                                minWidth: 150,
                                '& .MuiInputBase-input': { color: '#2B1810', fontSize: '12px', py: 0.5 },
                                '& option': { color: '#111', backgroundColor: '#fff' }
                              }}
                            >
                              <option value="all">All</option>
                              {categoryOptions.map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </TextField>
                          ) : col.key === 'division' ? (
                            <TextField
                              size="small"
                              select
                              SelectProps={{ native: true }}
                              value={divisionFilter}
                              onChange={(e) => {
                                setDivisionFilter(e.target.value);
                                setPage(0);
                              }}
                              sx={{
                                minWidth: 120,
                                '& .MuiInputBase-input': { color: '#2B1810', fontSize: '12px', py: 0.5 },
                                '& option': { color: '#111', backgroundColor: '#fff' }
                              }}
                            >
                              <option value="all">All</option>
                              {divisionOptions.map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </TextField>
                          ) : col.key === 'group' ? (
                            <TextField
                              size="small"
                              select
                              SelectProps={{ native: true }}
                              value={groupFilter}
                              onChange={(e) => {
                                setGroupFilter(e.target.value);
                                setPage(0);
                              }}
                              sx={{
                                minWidth: 120,
                                '& .MuiInputBase-input': { color: '#2B1810', fontSize: '12px', py: 0.5 },
                                '& option': { color: '#111', backgroundColor: '#fff' }
                              }}
                            >
                              <option value="all">All</option>
                              {groupOptions.map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </TextField>
                          ) : col.key === 'exclude' ? (
                            <TextField
                              size="small"
                              select
                              SelectProps={{ native: true }}
                              value={excludeFilter}
                              onChange={(e) => {
                                setExcludeFilter(e.target.value);
                                setPage(0);
                              }}
                              sx={{
                                minWidth: 100,
                                '& .MuiInputBase-input': { color: '#2B1810', fontSize: '12px', py: 0.5 },
                                '& option': { color: '#111', backgroundColor: '#fff' }
                              }}
                            >
                              <option value="all">All</option>
                              <option value="included">Included</option>
                              <option value="excluded">Excluded</option>
                            </TextField>
                          ) : (
                            <TextField
                              size="small"
                              variant="outlined"
                              value={
                                col.key === 'transactiondate'
                                  ? transactionDateFilter
                                  : col.key === 'date_added'
                                    ? dateAddedFilter
                                    : col.key === 'description'
                                      ? descriptionFilter
                                      : col.key === 'amount'
                                        ? amountFilter
                                        : col.key === 'auto_category'
                                          ? autoCategoryFilter
                                          : col.key === 'manual_category'
                                            ? manualCategoryFilter
                                            : col.key === 'class'
                                              ? classFilter
                                              : ''
                              }
                              onChange={(e) => {
                                const value = e.target.value;
                                if (col.key === 'transactiondate') setTransactionDateFilter(value);
                                else if (col.key === 'date_added') setDateAddedFilter(value);
                                else if (col.key === 'description') setDescriptionFilter(value);
                                else if (col.key === 'amount') setAmountFilter(value);
                                else if (col.key === 'auto_category') setAutoCategoryFilter(value);
                                else if (col.key === 'manual_category') setManualCategoryFilter(value);
                                else if (col.key === 'class') setClassFilter(value);
                                setPage(0);
                              }}
                              placeholder="Filter"
                              sx={{
                                minWidth: col.key === 'description' ? 280 : ['transactiondate', 'date_added'].includes(col.key) ? 120 : ['source', 'amount'].includes(col.key) ? 65 : 100,
                                '& .MuiInputBase-input': { color: '#2B1810', fontSize: '12px', py: 0.5 }
                              }}
                            />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredTransactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((tx, rowIdx) => {
                      const txId = String(tx?._id ?? tx?.id ?? rowIdx);
                      return (
                      <TableRow
                        key={tx?._id || tx?.id || rowIdx}
                        sx={{ '& td': { borderColor: '#D4B896' }, '&:hover': { backgroundColor: '#F0E3D0' }, ...(selectedIds.has(txId) ? { backgroundColor: 'rgba(244,167,185,0.15)' } : {}) }}
                      >
                        <TableCell padding="checkbox" sx={{ borderColor: '#D4B896' }}>
                          <Checkbox
                            checked={selectedIds.has(txId)}
                            onChange={(e) => {
                              setSelectedIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(txId);
                                else next.delete(txId);
                                return next;
                              });
                            }}
                            sx={{ color: '#2B1810', '&.Mui-checked': { color: '#F4A7B9' }, padding: '2px' }}
                          />
                        </TableCell>
                        {columns.map((col) => (
                          <TableCell
                            key={`${rowIdx}-${col.key}`}
                            padding={['auto_exclude', 'manual_exclude', 'final_exclude'].includes(col.key) ? 'checkbox' : 'normal'}
                            sx={{
                              color: '#2B1810',
                              fontSize: '12px',
                              padding: ['auto_exclude', 'manual_exclude', 'final_exclude'].includes(col.key) ? undefined : '6px 4px',

                              maxWidth: col.key === 'description' ? 280 : ['transactiondate', 'date_added'].includes(col.key) ? 120 : ['source', 'amount'].includes(col.key) ? 65 : ['manual_exclude'].includes(col.key) ? 50 : 100,
                              textOverflow: col.key === 'description' ? 'clip' : 'ellipsis',
                              whiteSpace: col.key === 'description' ? 'normal' : ['division', 'group', 'class'].includes(col.key) ? 'normal' : 'nowrap',
                              wordBreak: col.key === 'description' ? 'break-word' : ['division', 'group', 'class'].includes(col.key) ? 'break-word' : 'normal',
                              overflowWrap: col.key === 'description' ? 'anywhere' : ['division', 'group', 'class'].includes(col.key) ? 'break-word' : 'normal',
                              lineHeight: col.key === 'description' ? 1.35 : 'normal'
                            }}
                            title={!['auto_exclude', 'manual_exclude'].includes(col.key) ? getDisplayedCellValue(tx, col.key) : undefined}
                          >
                            {col.key === 'auto_exclude' ? (
                              <Checkbox
                                checked={Boolean(tx.auto_exclude)}
                                disabled={true}
                                color="default"
                                sx={{
                                  color: '#2B1810',
                                  '&.Mui-checked': { color: '#2B1810' },
                                  '& .MuiSvgIcon-root': { color: '#2B1810', fontSize: 18 }
                                }}
                              />
                            ) : col.key === 'manual_exclude' ? (
                              <Checkbox
                                checked={Boolean(tx.manual_exclude)}
                                onChange={(event, checked) => handleToggleManualExclude(tx, checked)}
                                disabled={loading || Boolean(updating[String(tx?._id ?? tx?.id ?? rowIdx)])}
                                color="default"
                                sx={{
                                  color: '#2B1810',
                                  '&.Mui-checked': { color: '#2B1810' },
                                  '& .MuiSvgIcon-root': { color: '#2B1810', fontSize: 18 }
                                }}
                              />
                            ) : col.key === 'manual_category' ? (
                              <Autocomplete
                                key={String(tx?._id ?? tx?.id ?? rowIdx)}
                                options={manualCategoryOptions}
                                value={tx.manual_category || null}
                                onChange={(e, newValue) => {
                                  const value = newValue || '';
                                  const itemId = String(tx?._id ?? tx?.id ?? rowIdx);
                                  setTransactions((prev) =>
                                    prev.map((item) =>
                                      String(item?._id ?? item?.id ?? '') === itemId
                                        ? {
                                            ...item,
                                            manual_category: value,
                                            final_category: value.trim() ? value.trim() : item.auto_category || null
                                          }
                                        : item
                                    )
                                  );
                                  handleManualCategorySave(tx, value);
                                }}
                                disabled={loading || Boolean(updating[String(tx?._id ?? tx?.id ?? rowIdx)])}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                      ...params.InputProps,
                                      style: { color: '#2B1810', fontSize: '12px', padding: '4px 8px' }
                                    }}
                                    sx={{
                                      '& .MuiInputBase-input': { color: '#2B1810', padding: '4px 8px', fontSize: '12px' },
                                      '& .MuiOutlinedInput-root': {
                                        color: '#2B1810',
                                        '& fieldset': {
                                          borderColor: '#C47A30',
                                          borderWidth: '1px'
                                        },
                                        '&:hover fieldset': {
                                          borderColor: '#7A5035'
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: '#F4A7B9',
                                          borderWidth: '2px'
                                        }
                                      }
                                    }}
                                  />
                                )}
                                slotProps={{
                                  paper: {
                                    sx: {
                                      backgroundColor: '#FFFFFF',
                                      color: '#2B1810',
                                      zIndex: 1400,
                                      '& .MuiAutocomplete-option': {
                                        fontSize: '12px',
                                        '&[aria-selected="true"]': { backgroundColor: 'rgba(244,167,185,0.3)' },
                                        '&.Mui-focused': { backgroundColor: '#F0E3D0' },
                                      }
                                    }
                                  },
                                  popper: {
                                    sx: {
                                      zIndex: 1400
                                    }
                                  }
                                }}
                                sx={{
                                  maxWidth: 100,
                                  width: '100%',
                                  '& .MuiAutocomplete-clearIndicator': { color: '#7A5035', padding: '0', fontSize: '16px' },
                                  '& .MuiAutocomplete-popupIndicator': { color: '#7A5035', padding: '0', fontSize: '16px' },
                                  '& .MuiOutlinedInput-root': {
                                    overflow: 'hidden'
                                  }
                                }}
                                size="small"
                                clearOnEscape
                              />
                            ) : col.key === 'comment' ? (
                              <TextField
                                size="small"
                                value={tx.comment || ''}
                                onChange={(e) => {
                                  const newComment = e.target.value;
                                  const itemId = String(tx?._id ?? tx?.id ?? rowIdx);
                                  setTransactions((prev) =>
                                    prev.map((item) =>
                                      String(item?._id ?? item?.id ?? '') === itemId
                                        ? { ...item, comment: newComment }
                                        : item
                                    )
                                  );
                                }}
                                onBlur={(e) => {
                                  handleCommentSave(tx, e.target.value);
                                }}
                                disabled={loading || Boolean(updating[String(tx?._id ?? tx?.id ?? rowIdx)])}
                                placeholder="Add comment..."
                                sx={{
                                  minWidth: 150,
                                  '& .MuiInputBase-input': {
                                    color: '#2B1810',
                                    padding: '4px 8px',
                                    fontSize: '12px'
                                  }
                                }}
                              />
                            ) : col.key === 'final_category' ? (
                              tx.final_category || tx.auto_category || ''
                            ) : col.key === 'auto_category' ? (
                              tx.auto_category || ''
                            ) : (
                              getDisplayedCellValue(tx, col.key)
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    );})}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ backgroundColor: '#F0E3D0', borderTop: '1px solid #D4B896', flexWrap: 'wrap' }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 2 }}>
                  <ASTIText text="Go to page" />
                  <TextField
                    size="small"
                    type="number"
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value)}
                    onBlur={handlePageInputCommit}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handlePageInputCommit();
                        e.currentTarget.blur();
                      }
                    }}
                    inputProps={{ min: 1, max: totalPages, style: { textAlign: 'center' } }}
                    sx={{ width: 70, '& .MuiInputBase-input': { color: '#2B1810', py: '4px' } }}
                  />
                  <ASTIText text={`of ${totalPages}`} />
                </Stack>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={filteredTransactions.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                  sx={{ color: '#2B1810', border: 'none', '& .MuiSvgIcon-root': { color: '#2B1810' }, '& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { color: '#7A5035' } }}
                />
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
